import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    ServingContainerInput,
} from '@barboxx/admin-components';
import { BUSINESS_SERVING_CONTAINER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessServingContainer } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    servingContainer: BusinessServingContainer;
}

export const ServingContainerDetailsCard = ({ servingContainer }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notification = useNotifications();

    const mutateServingContainer = useMutation({
        mutationKey: ['businessServingContainer', servingContainer.id],
        mutationFn: (data: BusinessServingContainer.UpdateRequest) =>
            Api.BusinessSpecific.ServingContainer.putServingContainer(
                servingContainer.id,
                data
            ),
    });

    const formContext = useForm<BusinessServingContainer.UpdateRequest>({
        resolver: yupResolver(BUSINESS_SERVING_CONTAINER_SCHEMA),
        defaultValues: servingContainer,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessServingContainer.UpdateRequest) =>
        mutateServingContainer
            .mutateAsync(values)
            .then(({ data }) => {
                notification.show('Updated serving container!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notification.show('Error Updating serving container!', {
                    severity: 'error',
                });
            });

    return (
        <Card>
            <CardHeader title="ServingContainer Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <ServingContainerInput disabled={!editable} />
                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            type="number"
                            fullWidth
                            disabled={!editable}
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <CheckboxElement
                            label="Active"
                            name="isActive"
                            disabled={!editable}
                        />

                        <Can I="update" a="BusinessServingContainer">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
